import React, { useEffect, useRef, useState } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { SITENAMEALIAS } from "../utils/init";
import { currentPageAccess, showToast } from "./library";

/**
 * ReadOnlyShell — wraps a routed page and enforces READ-only access.
 *
 * When the current page's access entry in `access_list` is "READ":
 *   1. A small banner is shown at the top of the page.
 *   2. Click / submit / change events on common write controls are
 *      intercepted at the capture phase and a toast is shown instead.
 *   3. Those controls are visually faded via CSS so the user can see
 *      they are disabled.
 *
 * To opt a control out of this enforcement (e.g. a search input,
 * pagination button, modal close button, sort toggle, navigation link),
 * add `data-readonly-allow="true"` to the element (or any ancestor up
 * to the shell root).
 */
const ReadOnlyShell = ({ children }) => {
  const location = useLocation();
  const wrapperRef = useRef(null);
  const [permission, setPermission] = useState("WRITE");

  // Re-evaluate permission whenever the route changes
  useEffect(() => {
    try {
      setPermission(currentPageAccess());
    } catch (e) {
      setPermission("WRITE");
    }
  }, [location.pathname]);

  // Capture-phase event interception
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    if (permission !== "READ") return;

    const isAllowed = (target) => {
      if (!target || target === node) return false;
      // Walk up to the shell root checking opt-outs
      let el = target;
      while (el && el !== node) {
        if (
          el.getAttribute &&
          el.getAttribute("data-readonly-allow") === "true"
        ) {
          return true;
        }
        el = el.parentNode;
      }
      return false;
    };

    const isWriteControl = (target) => {
      if (!target || !target.tagName) return false;
      const tag = target.tagName.toLowerCase();

      if (tag === "button") {
        const type = (target.getAttribute("type") || "button").toLowerCase();
        if (type === "submit") return true;
        const cls =
          target.className && typeof target.className === "string"
            ? target.className
            : "";
        // Common write button classes used across the app
        if (
          /\b(btn-primary|btn-success|btn-danger|btn-warning|btn-add|btn-save|btn-create|btn-edit|btn-delete|btn-update|btn-submit)\b/.test(
            cls
          )
        ) {
          return true;
        }
        return false;
      }
      if (tag === "input") {
        const type = (target.getAttribute("type") || "text").toLowerCase();
        if (type === "submit" || type === "button") return true;
        if (type === "checkbox" || type === "radio" || type === "file")
          return true;
        // Plain text-like inputs are blocked too — readers shouldn't edit forms
        if (
          [
            "text",
            "number",
            "email",
            "password",
            "tel",
            "url",
            "date",
            "datetime-local",
            "time",
          ].includes(type)
        ) {
          return true;
        }
        return false;
      }
      if (tag === "textarea" || tag === "select") return true;
      return false;
    };

    const findInteractiveAncestor = (target) => {
      let el = target;
      while (el && el !== node) {
        if (isWriteControl(el)) return el;
        el = el.parentNode;
      }
      return null;
    };

    const blockEvent = (e) => {
      const writeEl = findInteractiveAncestor(e.target);
      if (!writeEl) return;
      if (isAllowed(writeEl)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
      // Avoid spamming toasts on rapid events
      if (!blockEvent._lastToast || Date.now() - blockEvent._lastToast > 800) {
        blockEvent._lastToast = Date.now();
        showToast("error", "You have read-only access to this page");
      }
    };

    const blockSubmit = (e) => {
      if (isAllowed(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
      if (!blockEvent._lastToast || Date.now() - blockEvent._lastToast > 800) {
        blockEvent._lastToast = Date.now();
        showToast("error", "You have read-only access to this page");
      }
    };

    node.addEventListener("click", blockEvent, true);
    node.addEventListener("mousedown", blockEvent, true);
    node.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter" || e.key === " ") {
          const writeEl = findInteractiveAncestor(e.target);
          if (writeEl && !isAllowed(writeEl)) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      },
      true
    );
    node.addEventListener("change", blockEvent, true);
    node.addEventListener("submit", blockSubmit, true);

    return () => {
      node.removeEventListener("click", blockEvent, true);
      node.removeEventListener("mousedown", blockEvent, true);
      node.removeEventListener("change", blockEvent, true);
      node.removeEventListener("submit", blockSubmit, true);
    };
  }, [permission, location.pathname]);

  const isReadOnly = permission === "READ";

  return (
    <div
      ref={wrapperRef}
      className={
        isReadOnly
          ? "page-readonly-shell read-only-mode"
          : "page-readonly-shell"
      }
    >
      {isReadOnly && (
        <>
          <style>{`
                        .page-readonly-shell.read-only-mode .btn-primary,
                        .page-readonly-shell.read-only-mode .btn-success,
                        .page-readonly-shell.read-only-mode .btn-danger,
                        .page-readonly-shell.read-only-mode .btn-warning,
                        .page-readonly-shell.read-only-mode button[type="submit"],
                        .page-readonly-shell.read-only-mode input[type="submit"],
                        .page-readonly-shell.read-only-mode input[type="checkbox"],
                        .page-readonly-shell.read-only-mode input[type="radio"],
                        .page-readonly-shell.read-only-mode input[type="file"],
                        .page-readonly-shell.read-only-mode textarea,
                        .page-readonly-shell.read-only-mode select {
                            opacity: 0.55 !important;
                            cursor: not-allowed !important;
                        }
                        .page-readonly-shell.read-only-mode [data-readonly-allow="true"],
                        .page-readonly-shell.read-only-mode [data-readonly-allow="true"] * {
                            opacity: 1 !important;
                            cursor: auto !important;
                        }
                        .readonly-banner-wrap {
                            position: fixed;
                            top: 12px;
                            left: 0;
                            right: 0;
                            z-index: 9999;
                            display: flex;
                            justify-content: center;
                            pointer-events: none;
                        }
                        .readonly-banner {
                            background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
                            color: #fff;
                            text-align: center;
                            padding: 8px 18px;
                            font-size: 13px;
                            font-weight: 600;
                            letter-spacing: 0.3px;
                            border-radius: 999px;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                            display: inline-block;
                            pointer-events: auto;
                        }
                        .readonly-banner i {
                            margin-right: 6px;
                        }
                    `}</style>
          <div className="readonly-banner-wrap" data-readonly-allow="true">
            <div className="readonly-banner">
              <i className="mdi mdi-lock-outline"></i>
              You have read-only access to this page. Write actions are
              disabled.
            </div>
          </div>
        </>
      )}
      {children}
    </div>
  );
};

export default function ProtectedRoute({ component: Component, ...rest }) {
  const session = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
  const isAuthenticated = !!session;

  // Check if paybito launch platform cookies are available for auto-login
  const hasAutoLoginCookies =
    Cookies.get("paybito_launch_platform_token") &&
    Cookies.get("paybito_launch_platform_broker_id") &&
    Cookies.get("paybito_launch_platform_uuid");

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isAuthenticated) {
          return (
            <ReadOnlyShell>
              <Component {...props} />
            </ReadOnlyShell>
          );
        } else if (hasAutoLoginCookies) {
          // Store the full path including search params (excluding basepath)
          const fullPath = props.location.pathname + props.location.search;
          localStorage.setItem(SITENAMEALIAS + "_redirect_path", fullPath);
          return <Redirect to={{ pathname: "/autologin" }} />;
        } else {
          // Store the full path including search params (excluding basepath)
          const fullPath = props.location.pathname + props.location.search;
          localStorage.setItem(SITENAMEALIAS + "_redirect_path", fullPath);
          return <Redirect to={{ pathname: "/" }} />;
        }
      }}
    />
  );
}
